import { getSiteNameFromDomain } from './siteInfo';

/**
 * Central SEO engine.
 *
 * Keeps <title>, meta tags, the canonical link and JSON-LD structured data in
 * sync with the active route. Everything is derived from window.location so the
 * same bundle produces correct canonical/OG URLs on every brand domain.
 */

export const DEFAULT_SEO = {
  siteName: 'Online Betting Bangladesh',
  title: 'Online Cricket Betting, Live Casino & Slot Games in Bangladesh',
  description:
    'Play online cricket betting, live casino and slot games. Fast bKash, Nagad and Rocket deposits, quick BDT withdrawals and 24/7 customer support.',
  keywords:
    'online betting bangladesh, cricket betting, live casino, online casino bangladesh, slot games, bKash betting site, Nagad deposit, Rocket deposit, bdt casino, betting app download',
  robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  twitterCard: 'summary_large_image',
};

const TITLE_SEPARATOR = ' | ';
const JSON_LD_SELECTOR = 'script[type="application/ld+json"][data-seo-jsonld]';

const safeRead = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const capitalize = (value = '') => (value ? value.charAt(0).toUpperCase() + value.slice(1) : '');

/** Brand name shown in titles: API value first, then the domain label. */
export const getSiteBrand = () => {
  const stored = (safeRead('site_title') || '').trim();
  if (stored) return stored;
  const fromDomain = getSiteNameFromDomain();
  return fromDomain ? capitalize(fromDomain) : DEFAULT_SEO.siteName;
};

export const getSiteOrigin = () =>
  typeof window === 'undefined' ? '' : window.location.origin;

/** Logo/favicon used for OG images and Organization schema. */
export const getSiteLogo = () => {
  const stored = (safeRead('site_logo') || '').trim();
  if (stored) return stored;

  if (typeof document !== 'undefined') {
    const appleIcon = document.querySelector("link[rel='apple-touch-icon']");
    if (appleIcon && appleIcon.href) return appleIcon.href;
  }

  const origin = getSiteOrigin();
  return origin ? `${origin}/logo512.png` : '';
};

export const absoluteUrl = (path = '/') => {
  const origin = getSiteOrigin();
  if (!origin) return path;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${origin}${suffix}`;
};

/** Current path for canonical URLs, ignoring framework/branding parameters. */
export const currentCanonicalPath = () => {
  if (typeof window === 'undefined') return '/';
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  params.delete('app-frame');
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
};

export const socialProfilesToSameAs = (socialLinks = {}) =>
  ['facebook', 'instagram', 'youtube', 'telegram', 'twitter', 'tiktok']
    .map((key) => (socialLinks?.[key] || '').trim())
    .filter((url) => /^https?:\/\//i.test(url));

// ── DOM writers ─────────────────────────────────────────────────────────────

const findOrCreate = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
};

export const setMetaTag = (attr, key, content) => {
  if (typeof document === 'undefined' || !key || content == null || content === '') return;
  const el = findOrCreate(`meta[${attr}="${key}"]`, () => {
    const meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    return meta;
  });
  el.setAttribute('content', content);
};

export const setCanonicalLink = (href) => {
  if (typeof document === 'undefined' || !href) return;
  const el = findOrCreate('link[rel="canonical"]', () => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    return link;
  });
  el.setAttribute('href', href);
};

/**
 * True once a page has set its own SEO title, so late API/inline-script metadata
 * must not override it. Mirrored on window so the inline head script can respect it.
 */
export const hasSeoTitleApplied = () =>
  typeof window !== 'undefined' && window.__seoTitleApplied === true;

// ── Structured data builders ────────────────────────────────────────────────

export const buildOrganizationJsonLd = ({ brand, logo, sameAs } = {}) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brand || getSiteBrand(),
    url: `${getSiteOrigin()}/`,
  };
  if (logo) {
    data.logo = { '@type': 'ImageObject', url: logo };
    data.image = logo;
  }
  if (sameAs && sameAs.length) data.sameAs = sameAs;
  return data;
};

export const buildWebSiteJsonLd = ({ brand, description } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: brand || getSiteBrand(),
  url: `${getSiteOrigin()}/`,
  description: description || DEFAULT_SEO.description,
  inLanguage: 'en',
});

export const buildWebPageJsonLd = ({ type = 'WebPage', name, description, path } = {}) => ({
  '@context': 'https://schema.org',
  '@type': type,
  name,
  description,
  url: absoluteUrl(path || '/'),
  inLanguage: 'en',
});

export const buildBreadcrumbJsonLd = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const buildFaqJsonLd = (items = []) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: { '@type': 'Answer', text: item.answer },
  })),
});

let applyToken = 0;

const writeJsonLd = (blocks) => {
  if (typeof document === 'undefined') return;
  document.head.querySelectorAll(JSON_LD_SELECTOR).forEach((node) => node.remove());

  blocks.filter(Boolean).forEach((block) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', '');
    script.textContent = JSON.stringify(block);
    document.head.appendChild(script);
  });
};

const buildTitle = (title, brand) => {
  if (!title) return brand ? `${brand}${TITLE_SEPARATOR}${DEFAULT_SEO.title}` : DEFAULT_SEO.title;
  const alreadyBranded = brand && title.toLowerCase().includes(brand.toLowerCase());
  return alreadyBranded ? title : `${title}${TITLE_SEPARATOR}${brand}`;
};

/**
 * Applies all on-page SEO for the current route.
 * Returns a cleanup function that clears the structured data when the route changes.
 */
export const applySeo = ({
  title,
  description,
  keywords,
  path,
  image,
  type = 'website',
  robots,
  jsonLd = [],
  brand,
  sameAs,
} = {}) => {
  if (typeof document === 'undefined') return () => {};

  const siteBrand = (typeof brand === 'string' && brand.trim()) || getSiteBrand();
  const pagePath = path || currentCanonicalPath();
  const url = absoluteUrl(pagePath);
  const finalDescription = description || DEFAULT_SEO.description;
  const finalTitle = buildTitle(title, siteBrand);
  const ogImage = image || getSiteLogo();
  const token = ++applyToken;

  document.title = finalTitle;
  window.__seoTitleApplied = true;

  setMetaTag('name', 'description', finalDescription);
  setMetaTag('name', 'keywords', keywords || DEFAULT_SEO.keywords);
  setMetaTag('name', 'robots', robots || DEFAULT_SEO.robots);

  setMetaTag('property', 'og:title', finalTitle);
  setMetaTag('property', 'og:description', finalDescription);
  setMetaTag('property', 'og:site_name', siteBrand);
  setMetaTag('property', 'og:type', type);
  setMetaTag('property', 'og:url', url);

  setMetaTag('name', 'twitter:card', DEFAULT_SEO.twitterCard);
  setMetaTag('name', 'twitter:title', finalTitle);
  setMetaTag('name', 'twitter:description', finalDescription);

  if (ogImage) {
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:image:alt', `${siteBrand} logo`);
    setMetaTag('name', 'twitter:image', ogImage);
    setMetaTag('name', 'twitter:image:alt', `${siteBrand} logo`);
  }

  setCanonicalLink(url);

  writeJsonLd([
    buildOrganizationJsonLd({ brand: siteBrand, logo: ogImage, sameAs }),
    buildWebSiteJsonLd({ brand: siteBrand, description: finalDescription }),
    ...jsonLd,
  ]);

  // Only the most recent apply may clear the markup, so a route swap can never
  // wipe the structured data of the page that just took over.
  return () => {
    if (typeof document === 'undefined' || token !== applyToken) return;
    document.head.querySelectorAll(JSON_LD_SELECTOR).forEach((node) => node.remove());
  };
};
